import React, { useState, useEffect } from 'react';
import { HiOutlinePencil, HiOutlineTrash, HiOutlinePlus, HiOutlineSearch } from 'react-icons/hi';
import { Link, useNavigate } from 'react-router-dom';
import DeleteConfirmModal from '../../common/DeleteConfirmModal';
import toastService from '../../../services/toast';
import bookService from '../../../services/book';
import { usePermissions } from '../../../contexts/PermissionsContext';

const BooksList = () => {
  const { hasPermission } = usePermissions();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [filters, setFilters] = useState({
    status: 'all',
    price: 'all'
  });

  const [books, setBooks] = useState([]);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [selectedBook, setSelectedBook] = useState(null);

  const loadBooks = async () => {
    try {
      setLoading(true);
      const response = await bookService.getBooks({
        search: searchQuery,
        ...filters
      });
      // التحقق من البيانات
      setBooks(response);
    } catch (error) {
      // خطأ في تحميل الكتب
      toastService.error('حدث خطأ أثناء تحميل الكتب');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadBooks();
  }, [searchQuery, filters]);

  const handleSearch = (e) => {
    setSearchQuery(e.target.value);
  };

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
  };

  const handleDelete = (book) => {
    setSelectedBook(book);
    setDeleteModalOpen(true);
  };

  const confirmDelete = async () => {
    if (!selectedBook) return;
    
    try {
      await bookService.deleteBook(selectedBook.id);
      setDeleteModalOpen(false);
      toastService.success('تم حذف الكتاب بنجاح');
      loadBooks(); // إعادة تحميل الكتب
    } catch (error) {
      console.error('Error deleting book:', error);
      toastService.error('حدث خطأ أثناء حذف الكتاب');
    } finally {
      setSelectedBook(null);
    }
  };

  const handleEdit = (book) => {
    navigate(`/admin/dashboard/books/edit/${book.id}`);
  };

  return (
    <div className="p-6">
      {/* رأس الصفحة */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm">
        <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700 flex justify-between items-center">
          <h1 className="text-lg font-medium text-gray-900 dark:text-white">الكتب</h1>
          {hasPermission && hasPermission('create_book') && (
            <Link 
              to="/admin/dashboard/books/create"
              className="text-primary-600 hover:text-primary-700 dark:text-primary-500 dark:hover:text-primary-400"
              title="إضافة كتاب"
            >
              <HiOutlinePlus className="w-6 h-6" />
            </Link>
          )}
        </div>

        {/* أدوات التصفية */}
        <div className="p-6 border-b border-gray-200 dark:border-gray-700">
          <div className="grid grid-cols-3 gap-4">
            <div>
              <label htmlFor="search" className="sr-only">بحث</label>
              <div className="relative">
                <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
                  <HiOutlineSearch className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  type="text"
                  id="search"
                  value={searchQuery}
                  onChange={handleSearch}
                  className="input-base w-full pr-10"
                  placeholder="ابحث عن كتاب..."
                />
              </div>
            </div>

            <div>
              <select
                value={filters.status}
                onChange={(e) => handleFilterChange('status', e.target.value)}
                className="input-base w-full"
              >
                <option value="all">جميع الحالات</option>
                <option value="published">منشور</option>
                <option value="draft">مسودة</option>
              </select>
            </div>

            <div>
              <select
                value={filters.price}
                onChange={(e) => handleFilterChange('price', e.target.value)}
                className="input-base w-full"
              >
                <option value="all">جميع الأسعار</option>
                <option value="free">مجاني</option>
                <option value="paid">مدفوع</option>
              </select>
            </div>
          </div>
        </div>

        {/* قائمة الكتب */}
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
            <thead className="bg-gray-50 dark:bg-gray-700/50">
              <tr>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  الكتاب
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  السعر
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  التحميلات
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  الحالة
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  الإجراءات
                </th>
              </tr>
            </thead>
            <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
              {loading ? (
                <tr>
                  <td colSpan="5" className="px-6 py-4 text-center text-gray-500 dark:text-gray-400">
                    جاري التحميل...
                  </td>
                </tr>
              ) : books.length === 0 ? (
                <tr>
                  <td colSpan="5" className="px-6 py-4 text-center text-gray-500 dark:text-gray-400">
                    لا توجد كتب
                  </td>
                </tr>
              ) : (
                books.map((book) => (
                  <tr key={book.id}>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className="flex-shrink-0 h-10 w-10">
                          <img
                            className="h-10 w-10 rounded-lg object-cover"
                            src={book.cover_url || '/placeholder-book.svg'}
                            alt={book.title}
                          />
                        </div>
                        <div className="mr-4">
                          <div className="text-sm font-medium text-gray-900 dark:text-white">
                            {book.title}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="text-sm text-gray-900 dark:text-white">
                        {book.price === 0 ? 'مجاني' : `${book.price} ريال`}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="text-sm text-gray-900 dark:text-white">
                        {book.downloads}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                        book.status === 'published'
                          ? 'bg-green-100 text-green-800 dark:bg-green-800/20 dark:text-green-500'
                          : 'bg-yellow-100 text-yellow-800 dark:bg-yellow-800/20 dark:text-yellow-500'
                      }`}>
                        {book.status === 'published' ? 'منشور' : 'مسودة'}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-left text-sm font-medium">
                      <div className="flex items-center justify-end gap-2">
                        {hasPermission && hasPermission('edit_book') && (
                          <button
                            onClick={() => handleEdit(book)}
                            className="text-primary-600 hover:text-primary-700 dark:text-primary-500 dark:hover:text-primary-400"
                            title="تعديل"
                          >
                            <HiOutlinePencil className="w-5 h-5" />
                          </button>
                        )}
                        {hasPermission && hasPermission('delete_book') && (
                          <button
                            onClick={() => handleDelete(book)}
                            className="text-red-600 hover:text-red-700 dark:text-red-500 dark:hover:text-red-400"
                            title="حذف"
                          >
                            <HiOutlineTrash className="w-5 h-5" />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* رسالة تأكيد الحذف */}
      <DeleteConfirmModal
        isOpen={deleteModalOpen}
        onClose={() => setDeleteModalOpen(false)}
        onConfirm={confirmDelete}
        itemType="الكتاب"
        itemTitle={selectedBook?.title}
      />
    </div>
  );
};

export default BooksList;
